import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import ExcelDownload from "../../../assets/download-green.svg";
import SortDefault from "../../../assets/sort_default.svg";
import SortAsc from "../../../assets/sort_asc.svg";
import SortDsc from "../../../assets/sort_desc.svg";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  Table,
  Column as RSuiteColumn,
  ColumnGroup,
  HeaderCell,
  Cell,
} from "rsuite-table";
import moment from "moment";
import { Toggle, Tooltip, Whisper } from "rsuite";
import ProductInfoIcon from "../../../assets/info-circle-grey.svg";
import { EmptyMessageTemplate } from "../../compliance-monitoring/cost-compliance";
import { appInsights } from "../../../router/router";
type Props = {
  totalVendors: string[];
  totalDistricts: string[];
  selectedVendor: string;
  commodity: string;
  vendor: string;
  selectedDistrict: string;
  quotes: number;
  district: string;
  skuToggle: boolean;
  setSkuToggle: any;
  quoteToggle: boolean;
  setQuoteToggle: any;
  skuData: any[];
  vendorSummaryData: any[];
  downloadExcelRef?: MutableRefObject<(() => void) | null>;
};

const getColorForDiff = (diff: number) => {
  if (diff > 5) {
    return "text-alert";
  } else if (diff > 1.01 && diff < 5) {
    return "text-warning";
  } else if (diff === 0) {
    return "-";
  } else {
    return "text-deloitteGreen";
  }
};

const roundDifferenceValues = (data: any[]) =>
  Array.isArray(data)
    ? data.map((row: any) => {
        if (!row || typeof row !== "object") {
          return row;
        }
        const updatedRow = { ...row };
        Object.keys(updatedRow).forEach((key) => {
          if (key.endsWith("_difference")) {
            const value = updatedRow[key];
            if (
              value !== null &&
              value !== undefined &&
              value !== "" &&
              !Number.isNaN(Number(value))
            ) {
              updatedRow[key] = Number(Number(value).toFixed(1));
            }
          }
        });
        return updatedRow;
      })
    : data;
const SkuAnalysis = ({
  totalVendors,
  selectedVendor,
  selectedDistrict,
  totalDistricts,
  setSkuToggle,
  setQuoteToggle,
  quoteToggle,
  commodity,
  vendor,
  quotes,
  district,
  skuToggle,
  skuData,
  vendorSummaryData,
  downloadExcelRef,
}: Props) => {
  const [tableKey, setTableKey] = useState(0); // State to force re-render
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>([]);
  const [tableDimensions, setTableDimensions] = useState({
    height: 310,
    rowHeight: 28,
  });
  useEffect(() => {
    const handleResize = () => {
      setTableKey((prevKey) => prevKey + 1); // Update key to trigger re-render
      if (window.innerWidth > 1600) {
        setTableDimensions({
          height: 450,
          rowHeight: 32,
        });
      } else if (window.innerWidth === 1600) {
        setTableDimensions({
          height: 350,
          rowHeight: 28,
        });
      } else if (window.innerWidth === 1280) {
        setTableDimensions({
          height: 310,
          rowHeight: 28,
        });
      } else if (window.innerWidth >= 1024 && window.innerWidth <= 1164) {
        setTableDimensions({
          height: 250,
          rowHeight: 20,
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize); // Clean up
    };
  }, []);

  useEffect(() => {
    setTableData(roundDifferenceValues(skuData));
  }, [skuData]);

  const handleSortColumn = (newSortColumn: any) => {
    let nextSortType: any;
    if (sortColumn === newSortColumn) {
      // Cycle: asc -> desc -> reset (null)
      if (sortType === "asc") {
        nextSortType = "desc";
      } else if (sortType === "desc") {
        nextSortType = null; // Reset to original data
      } else {
        nextSortType = "asc";
      }
    } else {
      // If a new column is selected, always start with ascending order
      nextSortType = "asc";
    }
    setSortColumn(newSortColumn);
    setSortType(nextSortType);
    if (!nextSortType) {
      setTableData(roundDifferenceValues(skuData)); // Reset if cycle reaches `null`
      return;
    }
    const sortedData = [...tableData].sort((a, b) => {
      let valueA = a[newSortColumn];
      let valueB = b[newSortColumn];
      if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }
      if (valueA > valueB) return nextSortType === "asc" ? 1 : -1;
      if (valueA < valueB) return nextSortType === "asc" ? -1 : 1;
      return 0;
    });
    setTableData(sortedData);
  };

  const handleExcelDownload = async (skuToggle: boolean) => {
    try {
      if (!Array.isArray(tableData) || tableData.length === 0) {
        return;
      }
      if (!quoteToggle) {
      const rawData = tableData;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Supplier Quote Assessment");

      if (skuToggle) {
        // Extract unique districts dynamically
        const districtNames = Object.keys(rawData[0])
          .filter(
            (key) =>
              key !== "trade" &&
              key !== "size" &&
              key !== "benchmark" &&
              key !== "expected_quote" &&
              key !== "quotation" &&
              key !== "commodity" &&
              key !== "vendor" &&
              key !== "quote" &&
              !key.includes("_difference") &&
              !key.includes("_expected_quote") // This will exclude the expected_quote fields from district extraction
          )
          .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

        // Define headers dynamically
        let columns: any = [
          { header: "Supplier", key: "vendor", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];
        // Add district columns dynamically
        districtNames.forEach((district) => {
          columns.push(
            {
              header: `${district} Current Quote`,
              key: `${district}_current`,
              width: 18,
            },
            {
              header: `${district} Expected Quote`,
              key: `${district}_expected_quote`,
              width: 18,
            }
          );
        });
        // Set columns to worksheet
        worksheet.columns = columns?.map((item: any) => {
          if (
            item.header.includes("Current Quote") ||
            item.header.includes("Expected Quote")
          ) {
            item.style = { numFmt: "$#,##0.00" };
          }
          return item;
        });
        // Merge header cells dynamically
        let mergeStart = 6; // Columns start from D (1-based index)
        districtNames.forEach((district) => {
          const startCol = worksheet.getColumn(mergeStart).letter;
          const endCol = worksheet.getColumn(mergeStart + 1).letter;
          worksheet.mergeCells(`${startCol}1:${endCol}1`); // Merge cells for district
          worksheet.getCell(`${startCol}1`).value =
            district.charAt(0).toUpperCase() + district.slice(1);
          worksheet.getCell(`${startCol}1`).alignment = {
            horizontal: "center",
            vertical: "middle",
          };
          worksheet.getCell(`${startCol}1`).font = { bold: true, size: 12 };
          mergeStart += 2;
        });
        // Add data dynamically

        let secondRow = ["", "", "", "", ""];
        districtNames?.forEach(() => {
          secondRow.push("Current Quote");
          secondRow.push("Expected Quote");
        });
        worksheet.addRow(secondRow);
        rawData.forEach(
          (row: {
            [x: string]: any;
            trade: any;
            size: any;
            benchmark: any;
          }) => {
            let rowData: any = {
              trade: row.trade,
              size: row.size,
              benchmark: row.benchmark,
              commodity,
              quote: quotes,
              vendor: selectedVendor,
            };
            // Populate district-specific data
            districtNames.forEach((district) => {
              rowData[`${district}_current`] = row[district] || "";
              rowData[`${district}_expected_quote`] =
                row[`${district}_expected_quote`] || "";
            });
            worksheet.addRow(rowData);
          }
        );
      } else {
        // Extract unique vendors dynamically
        const vendorName = Object.keys(rawData[0])
          .filter(
            (key) =>
              key !== "trade" &&
              key !== "size" &&
              key !== "benchmark" &&
              key !== "expected_quote" &&
              key !== "quotation" &&
              key !== "commodity" &&
              key !== "district" &&
              key !== "quote" &&
              !key.includes("_difference") &&
              !key.includes("_expected_quote") // This will exclude the expected_quote fields from district extraction
          )
          .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
        // Define headers dynamically
        let columns: any = [
          { header: "District", key: "district", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];
        // Add vendor columns dynamically
        vendorName.forEach((vendor) => {
          columns.push(
            {
              header: `${vendor} Current Quote`,
              key: `${vendor}_current`,
              width: 18,
            },
            {
              header: `${vendor} Expected Quote`,
              key: `${vendor}_expected_quote`,
              width: 18,
            }
          );
        });
        // Set columns to worksheet
        worksheet.columns = columns?.map((item: any) => {
          if (
            item.header.includes("Current Quote") ||
            item.header.includes("Expected Quote")
          ) {
            item.style = { numFmt: "$#,##0.00" };
          }
          return item;
        });
        // Merge header cells dynamically
        let mergeStart = 6; // Columns start from D (1-based index)
        vendorName.forEach((vendor: any) => {
          const startCol = worksheet.getColumn(mergeStart).letter;
          const endCol = worksheet.getColumn(mergeStart + 1).letter;
          worksheet.mergeCells(`${startCol}1:${endCol}1`); // Merge cells for vendor
          worksheet.getCell(`${startCol}1`).value = `${vendor.charAt(0).toUpperCase() + vendor.slice(1)
            }${vendorSummaryData[vendor?.toUpperCase()]
              ? vendorSummaryData[vendor.toUpperCase()].quotation_no === 10
                ? ": Mid Month"
                : `: Quote #${vendorSummaryData[vendor.toUpperCase()].quotation_no
                }`
              : ""
            }`;
          worksheet.getCell(`${startCol}1`).alignment = {
            horizontal: "center",
            vertical: "middle",
          };
          worksheet.getCell(`${startCol}1`).font = { bold: true, size: 12 };
          mergeStart += 2;
        });
        // Add data dynamically

        let secondRow = ["", "", "", "", ""];
        vendorName?.forEach(() => {
          secondRow.push("Current Quote");
          secondRow.push("Expected Quote");
        });
        worksheet.addRow(secondRow);
        rawData.forEach(
          (row: {
            [x: string]: any;
            trade: any;
            size: any;
            benchmark: any;
          }) => {
            let rowData: any = {
              trade: row.trade,
              size: row.size,
              benchmark: row.benchmark,
              commodity,
              quote: quotes,
              district: selectedDistrict,
            };
            // Populate vendor-specific data
            vendorName.forEach((vendor) => {
              rowData[`${vendor}_current`] = row[vendor] || "";
              rowData[`${vendor}_expected_quote`] =
                row[`${vendor}_expected_quote`] || "";
            });
            worksheet.addRow(rowData);
          }
        );
      }

      // Save File
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `SKU Ratio.xlsx`);
      appInsights.trackEvent({
        name: "Reports: Trend Reports",
        properties: {
          description: `${localStorage.getItem(
            "userEmail"
          )} has downloaded "SKU Ratio.xlsx" Report`,
        },
      });
    } else {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Supplier Quote Assessment");
      if (!skuToggle && totalVendors?.length > 0) {
        let columns = [
          { header: "District", key: "district", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];
        totalVendors
          .filter(
            (vendor) =>
              vendor !== null && vendor !== undefined && vendor !== "null"
          )
          .forEach((vendor: any) => {
            columns.push({
              header: `${vendor?.charAt(0).toUpperCase()}${vendor
                ?.slice(1)
                .toLowerCase()}${vendorSummaryData[vendor?.toUpperCase()]
                  ? vendorSummaryData[vendor.toUpperCase()].quotation_no === 10
                    ? ": Mid Month"
                    : `: Quote #${vendorSummaryData[vendor.toUpperCase()].quotation_no
                    }`
                  : ""
                }`,
              key: `${vendor?.toLowerCase()}`,
              width: 18,
            });
          });
        // Set columns to worksheet
        worksheet.columns = columns;
        tableData?.map((item: any) => {
          let rowData: any = {
            commodity: commodity,
            district: selectedDistrict,
            trade: item?.trade,
            size: item.size,
            benchmark: item.benchmark,
          };
          // Populate vendor-specific data
          totalVendors.forEach((vendor) => {
            rowData[`${vendor?.toLowerCase()}`] =
              item[vendor?.toLowerCase()] !== undefined
                ? item[vendor?.toLowerCase()]
                : "";
          });
          worksheet.addRow(rowData)?.eachCell((cell) => {
            cell.alignment = { horizontal: "left" };
          });
        });
      }
      if (skuToggle && totalDistricts?.length > 0) {
        let columns = [
          { header: "Supplier", key: "vendor", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];

        totalDistricts
          .filter(
            (district) =>
              district !== null && district !== undefined && district !== "null"
          )
          .forEach((district) => {
            columns.push({
              header: `${district?.charAt(0).toUpperCase()}${district
                ?.slice(1)
                .toLowerCase()}`,
              key: `${district?.toLowerCase()}`,
              width: 18,
            });
          });
        // Set columns to worksheet
        worksheet.columns = columns;
        tableData?.map((item: any) => {
          let rowData: any = {
            commodity: commodity,
            quote: quotes,
            vendor: selectedVendor,
            trade: item?.trade,
            size: item.size,
            benchmark: item.benchmark,
          };
          // Populate district-specific data
          totalDistricts.forEach((district) => {
            rowData[`${district?.toLowerCase()}`] =
              item[district?.toLowerCase()] !== undefined
                ? item[district?.toLowerCase()]
                : "";
          });
          worksheet.addRow(rowData)?.eachCell((cell) => {
            cell.alignment = { horizontal: "left" };
          });
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `SKU Ratio.xlsx`);
      appInsights.trackEvent({
        name: "Vendor Quote Assessment: SKU Ratio Analysis",
        properties: {
          description: `${localStorage.getItem(
            "userEmail"
          )} has downloaded "SKU Ratio.xlsx" Report`,
        },
      });
    }
    } catch (error) {
      appInsights.trackException?.({ exception: error as Error });
    }
  };

  const handleExcelDownloadForWire = async (skuToggle: boolean) => {
    try {
      if (!Array.isArray(tableData) || tableData.length === 0) {
        return;
      }
      if (!quoteToggle) {
      const rawData = tableData;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Supplier Quote Assessment");

      if (skuToggle) {
        // Extract unique districts dynamically
        const districtNames = Object.keys(rawData[0])
          .filter(
            (key) =>
              key !== "trade" &&
              key !== "size" &&
              key !== "benchmark" &&
              key !== "expected_quote" &&
              key !== "quotation" &&
              key !== "commodity" &&
              key !== "vendor" &&
              key !== "quote" &&
              key !== "contract_id" &&
              key !== "contract_type" &&
              !key.includes("_difference") &&
              !key.includes("_expected_quote") // This will exclude the expected_quote fields from district extraction
          )
          .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

        // Define headers dynamically
        let columns: any = [
          { header: "Supplier", key: "vendor", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];
        // Add district columns dynamically
        districtNames.forEach((district) => {
          columns.push(
            {
              header: `${district} Current Quote`,
              key: `${district}_current`,
              width: 18,
            },
            {
              header: `${district} Expected Quote`,
              key: `${district}_expected_quote`,
              width: 18,
            }
          );
        });
        // Set columns to worksheet
        worksheet.columns = columns?.map((item: any) => {
          if (
            item.header.includes("Current Quote") ||
            item.header.includes("Expected Quote")
          ) {
            item.style = { numFmt: "$#,##0.00" };
          }
          return item;
        });
        // Merge header cells dynamically
        let mergeStart = 6; // Columns start from D (1-based index)
        districtNames.forEach((district) => {
          const startCol = worksheet.getColumn(mergeStart).letter;
          const endCol = worksheet.getColumn(mergeStart + 1).letter;
          worksheet.mergeCells(`${startCol}1:${endCol}1`); // Merge cells for district
          worksheet.getCell(`${startCol}1`).value =
            district.charAt(0).toUpperCase() + district.slice(1);
          worksheet.getCell(`${startCol}1`).alignment = {
            horizontal: "center",
            vertical: "middle",
          };
          worksheet.getCell(`${startCol}1`).font = { bold: true, size: 12 };
          mergeStart += 2;
        });
        // Add data dynamically

        let secondRow = ["", "", "", "", ""];
        districtNames?.forEach(() => {
          secondRow.push("Current Quote");
          secondRow.push("Expected Quote");
        });
        worksheet.addRow(secondRow);
        rawData.forEach(
          (row: {
            [x: string]: any;
            trade: any;
            size: any;
            benchmark: any;
          }) => {
            let rowData: any = {
              trade: row.trade,
              size: row.size,
              benchmark: row.benchmark,
              commodity,
              quote: quotes,
              vendor: selectedVendor,
            };
            // Populate district-specific data
            districtNames.forEach((district) => {
              rowData[`${district}_current`] = row[district] || "";
              rowData[`${district}_expected_quote`] =
                row[`${district}_expected_quote`] || "";
            });
            worksheet.addRow(rowData);
          }
        );
      } else {
        // Extract unique vendors dynamically
        const vendorName = Object.keys(rawData[0])
          .filter(
            (key) =>
              key !== "trade" &&
              key !== "size" &&
              key !== "benchmark" &&
              key !== "expected_quote" &&
              key !== "quotation" &&
              key !== "commodity" &&
              key !== "district" &&
              key !== "quote" &&
              key !== "contract_id" &&
              key !== "contract_type" &&
              !key.includes("_difference") &&
              !key.includes("_expected_quote") // This will exclude the expected_quote fields from district extraction
          )
          .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
        // Define headers dynamically
        let columns: any = [
          { header: "District", key: "district", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];
        // Add vendor columns dynamically
        vendorName.forEach((vendor) => {
          columns.push(
            {
              header: `${vendor} Current Quote`,
              key: `${vendor}_current`,
              width: 18,
            },
            {
              header: `${vendor} Expected Quote`,
              key: `${vendor}_expected_quote`,
              width: 18,
            }
          );
        });
        // Set columns to worksheet
        worksheet.columns = columns?.map((item: any) => {
          if (
            item.header.includes("Current Quote") ||
            item.header.includes("Expected Quote")
          ) {
            item.style = { numFmt: "$#,##0.00" };
          }
          return item;
        });
        // Merge header cells dynamically
        let mergeStart = 6; // Columns start from D (1-based index)

        vendorName.forEach((vendor: any) => {
          const startCol = worksheet.getColumn(mergeStart).letter;
          const endCol = worksheet.getColumn(mergeStart + 1).letter;
          worksheet.mergeCells(`${startCol}1:${endCol}1`); // Merge cells for vendor
          worksheet.getCell(`${startCol}1`).value = `${vendor.charAt(0).toUpperCase() + vendor.slice(1)
            }`;
          worksheet.getCell(`${startCol}1`).alignment = {
            horizontal: "center",
            vertical: "middle",
          };
          worksheet.getCell(`${startCol}1`).font = { bold: true, size: 12 };
          mergeStart += 2;
        });
        // Add data dynamically

        let secondRow = ["", "", "", "", ""];
        vendorName?.forEach(() => {
          secondRow.push("Current Quote");
          secondRow.push("Expected Quote");
        });
        worksheet.addRow(secondRow);
        rawData.forEach(
          (row: {
            [x: string]: any;
            trade: any;
            size: any;
            benchmark: any;
          }) => {
            let rowData: any = {
              trade: row.trade,
              size: row.size,
              benchmark: row.benchmark,
              commodity,
              quote: quotes,
              district: selectedDistrict,
            };
            // Populate vendor-specific data
            vendorName.forEach((vendor) => {
              rowData[`${vendor}_current`] = row[vendor] || "";
              rowData[`${vendor}_expected_quote`] =
                row[`${vendor}_expected_quote`] || "";
            });
            worksheet.addRow(rowData);
          }
        );
      }

      // Save File
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `SKU Ratio.xlsx`);
      appInsights.trackEvent({
        name: "Reports: Trend Reports",
        properties: {
          description: `${localStorage.getItem(
            "userEmail"
          )} has downloaded "SKU Ratio.xlsx" Report`,
        },
      });
    } else {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Supplier Quote Assessment");
      if (!skuToggle && totalVendors?.length > 0) {
        let columns = [
          { header: "District", key: "district", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];
        totalVendors
          .filter(
            (vendor) =>
              vendor !== null && vendor !== undefined && vendor !== "null"
          )
          .forEach((vendor: any) => {
            columns.push({
              header: `${vendor}`,
              key: `${vendor?.toLowerCase()}`,
              width: 18,
            });
          });
        // Set columns to worksheet
        worksheet.columns = columns;
        tableData?.map((item: any) => {
          let rowData: any = {
            commodity: commodity,
            district: selectedDistrict,
            trade: item?.trade,
            size: item.size,
            benchmark: item.benchmark,
          };
          // Populate vendor-specific data
          totalVendors.forEach((vendor) => {
            rowData[`${vendor?.toLowerCase()}`] =
              item[vendor?.toLowerCase()] !== undefined
                ? item[vendor?.toLowerCase()]
                : "";
          });
          worksheet.addRow(rowData)?.eachCell((cell) => {
            cell.alignment = { horizontal: "left" };
          });
        });
      }
      if (skuToggle && totalDistricts?.length > 0) {
        let columns = [
          { header: "Supplier", key: "vendor", width: 10 },
          { header: "Commodity", key: "commodity", width: 10 },
          { header: "Trade", key: "trade", width: 10 },
          { header: "Size", key: "size", width: 10 },
          { header: "Benchmark", key: "benchmark", width: 12 },
        ];

        totalDistricts
          .filter(
            (district) =>
              district !== null && district !== undefined && district !== "null"
          )
          .forEach((district) => {
            columns.push({
              header: `${district?.charAt(0).toUpperCase()}${district
                ?.slice(1)
                .toLowerCase()}`,
              key: `${district?.toLowerCase()}`,
              width: 18,
            });
          });
        // Set columns to worksheet
        worksheet.columns = columns;
        tableData?.map((item: any) => {
          let rowData: any = {
            commodity: commodity,
            quote: quotes,
            vendor: selectedVendor,
            trade: item?.trade,
            size: item.size,
            benchmark: item.benchmark,
          };
          // Populate district-specific data
          totalDistricts.forEach((district) => {
            rowData[`${district?.toLowerCase()}`] =
              item[district?.toLowerCase()] !== undefined
                ? item[district?.toLowerCase()]
                : "";
          });
          worksheet.addRow(rowData)?.eachCell((cell) => {
            cell.alignment = { horizontal: "left" };
          });
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `SKU Ratio.xlsx`);
      appInsights.trackEvent({
        name: "Vendor Quote Assessment: SKU Ratio Analysis",
        properties: {
          description: `${localStorage.getItem(
            "userEmail"
          )} has downloaded "SKU Ratio.xlsx" Report`,
        },
      });
    }
    } catch (error) {
      appInsights.trackException?.({ exception: error as Error });
    }
  };

  if (downloadExcelRef) {
    downloadExcelRef.current = () => {
      if (commodity === "Copper Wire") {
        handleExcelDownloadForWire(skuToggle);
      } else {
        handleExcelDownload(skuToggle);
      }
    };
  }

  return (
    <>
      <div className="w-full flex justify-between items-center shadow-module-card border border-[#bebebe] bg-white p-pd13 font-bold text-fs16 rounded-md basis-[5%]">
        <div className="text-[1.8vh] flex items-center">
          <div>SKU Ratio Analysis</div>{" "}
          {
            <>
              <div className="text-deloitteGreen  ml-[0.8vh]">
                for {skuToggle ? selectedVendor : selectedDistrict}
              </div>
              <div
                onClick={() => {
                  if (commodity === 'Copper Wire') {
                    handleExcelDownloadForWire(skuToggle)
                  } else {
                    handleExcelDownload(skuToggle);
                  }
                }}
                className="ml-[0.8vh] cursor-pointer"
              >
                {" "}
                <img className="h-[2.5vh]" src={ExcelDownload} />
              </div>
            </>
          }
        </div>
        <div className="flex gap-16">
          <div className="text-fs12">
            <span className={quoteToggle === false ? "font-bold" : ""}>
              Quote
            </span>
            <Toggle
              checked={quoteToggle}
              onChange={(value) => {
                setQuoteToggle(!quoteToggle);
              }}
              size={"md"}
              className="px-2"
              color="green"
            />
            <span className={quoteToggle === true ? "font-bold" : ""}>
              Ratio
            </span>
          </div>
          <div className="text-fs12">
            <span className={skuToggle === false ? "font-bold" : ""}>
              By Supplier
            </span>
            <Toggle
              checked={skuToggle}
              onChange={(value) => {
                setSkuToggle(!skuToggle);
              }}
              size={"md"}
              className="px-2"
              color="green"
            />
            <span className={skuToggle === true ? "font-bold" : ""}>
              By District
            </span>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-end items-center  p-[1vh 1vh 0.2vh 1vh] font-bold text-fs16 rounded-md basis-[2%]">
        <div>
          <div className="flex self-end justify-end items-center flex-nowrap gap-4">
            <div className="flex gap-1 items-center">
              <span className="bg-deloitteGreen w-[2.5vh] h-[2.5vh]"></span>
              <span className="font-semibold text-[1.5vh]">
                Variation up to 1% of expected cost
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="bg-[#E9C400] w-[2.5vh] h-[2.5vh]"></span>
              <span className="font-semibold text-[1.5vh]">
                Variation between 1% - 5% of expected cost
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="bg-[#CD6318] w-[2.5vh] h-[2.5vh]"></span>
              <span className="font-semibold text-[1.5vh]">
                Variation above 5% of expected cost
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="w-[79vw] sku-table h-[55vh]">
        {tableData && tableData?.length !== 0 ? (
          <Table
            headerHeight={80}
            data={tableData}
            fillHeight
            bordered
            rowClassName={(rowData) => {
              return (rowData?.benchmark === 1 && rowData?.trade === "EMT" && rowData?.size === "3/4") ||
                (rowData?.benchmark === 1 && rowData?.trade === "PVC 40" && rowData?.size === "4") ||
                (rowData?.benchmark === 1 && rowData?.trade === "Bare Copper" && rowData?.size === "BARE-Stranded-4/0 AWG") ||
                (rowData?.benchmark === 1 && rowData?.trade === "Non-Metallic" && rowData?.size === "NM-WG-10 AWG-3-C") ||
                (rowData?.benchmark === 1 && rowData?.trade === "NM" && rowData?.size === "NM-WG-10 AWG-3-C") ||
                (rowData?.benchmark === 1 && rowData?.trade === "THHN" && rowData?.size === "THHN-Stranded-12 AWG") ||
                (rowData?.benchmark === 1 && rowData?.trade === "THHN" && rowData?.size === "THHN-Stranded-500 MCM") ||
                (rowData?.benchmark === 1 && rowData?.trade === "XLP-USE" && rowData?.size === "XLP-USE-Stranded-6 AWG") ||
                (rowData?.benchmark === 1 && rowData?.trade === "XHHW" && rowData?.size === "XHHW-2-Stranded-600 MCM")
                ? "highlight-row"
                : "";
            }}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            rowHeight={tableDimensions.rowHeight}
            cellBordered
            hover={false}
          >
            <RSuiteColumn
              flexGrow={1}
              minWidth={commodity === 'Copper Wire' ? 125 : 120}
              fixed
              verticalAlign="middle"
            >
              <HeaderCell>Trade</HeaderCell>
              <Cell dataKey="trade" />
            </RSuiteColumn>
            <RSuiteColumn
              flexGrow={1}
              minWidth={commodity === 'Copper Wire' ? 250 : 120}
              fixed
              verticalAlign="middle"
            >
              <HeaderCell>Size</HeaderCell>
              <Cell dataKey="size" />
            </RSuiteColumn>
            <RSuiteColumn
              flexGrow={1}
              minWidth={commodity === 'Copper Wire' ? 105 : 120}
              fixed
              verticalAlign="middle"
            >
              <HeaderCell>
                <div className="w-full flex justify-start gap-1">
                  <span>Benchmark</span>
                  <Whisper
                    className="inline"
                    placement="top"
                    speaker={
                      <Tooltip placement="auto">
                        Benchmark ratio is determined by identifying the lowest
                        ratio offered by any vendor over the past three months
                      </Tooltip>
                    }
                  >
                    <img
                      src={ProductInfoIcon}
                      alt="info"
                      className="w-[1.75vh] cursor-pointer inline"
                    ></img>
                  </Whisper>
                </div>
              </HeaderCell>
              <Cell dataKey="benchmark">
                {(rowData) =>
                  `${rowData.benchmark ? `${rowData.benchmark}` : "-"}`
                }
              </Cell>
            </RSuiteColumn>
            {skuToggle &&
              totalDistricts?.length > 0 &&
              totalDistricts?.map((item: any) => {
                return (
                  <>
                    {quoteToggle ? (
                      <>
                        {totalDistricts?.length >= 1 &&
                          totalDistricts?.length <= 3 ? (
                          <RSuiteColumn
                            // sortable
                            width={180}
                            flexGrow={1}
                            align="left"
                            verticalAlign="middle"
                          >
                            <HeaderCell>{item}</HeaderCell>
                            <Cell
                              dataKey={`${item?.toLowerCase()}_difference`}
                              className="flex justify-between items-center"
                            >
                              {(rowData) => (
                                <div className="flex w-full justify-between items-center">
                                  <div>
                                    {rowData[item?.toLowerCase()]
                                      ? `${rowData[item?.toLowerCase()]}`
                                      : "-"}
                                  </div>
                                  <div
                                    className={`${getColorForDiff(
                                      rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ]
                                    )}`}
                                  >
                                    {rowData[
                                      `${item?.toLowerCase()}_difference`
                                    ]
                                      ? rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ] + "%"
                                      : "-"}
                                  </div>
                                </div>
                              )}
                            </Cell>
                          </RSuiteColumn>
                        ) : (
                          <RSuiteColumn
                            // sortable
                            width={180}
                            align="left"
                            verticalAlign="middle"
                          >
                            <HeaderCell>{item}</HeaderCell>
                            <Cell
                              dataKey={`${item?.toLowerCase()}_difference`}
                              className="flex justify-between items-center"
                            >
                              {(rowData) => (
                                <div className="flex w-full justify-between items-center">
                                  <div>
                                    {rowData[item?.toLowerCase()]
                                      ? // Number(
                                      rowData[item?.toLowerCase()]
                                      : // ).toFixed(2)
                                      "-"}
                                  </div>
                                  <div
                                    className={`${getColorForDiff(
                                      rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ]
                                    )}`}
                                  >
                                    {rowData[
                                      `${item?.toLowerCase()}_difference`
                                    ]
                                      ? rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ] + "%"
                                      : "-"}
                                  </div>
                                </div>
                              )}
                            </Cell>
                          </RSuiteColumn>
                        )}
                      </>
                    ) : (
                      <ColumnGroup
                        align="center"
                        verticalAlign="middle"
                        groupHeaderHeight={40}
                        header={item}
                      >
                        {totalDistricts?.length >= 1 &&
                          totalDistricts?.length <= 3 ? (
                          <>
                            <RSuiteColumn
                              sortable
                              width={180}
                              flexGrow={1}
                              align="left"
                              verticalAlign="middle"
                            >
                              <HeaderCell
                                renderSortIcon={(sortType) => {
                                  if (sortType === "asc") {
                                    return <img src={SortAsc} />;
                                  } else if (sortType === "desc") {
                                    return <img src={SortDsc} />;
                                  } else return <img src={SortDefault} />;
                                }}
                              >
                                Current Quote
                              </HeaderCell>
                              <Cell
                                dataKey={`${item?.toLowerCase()}_difference`}
                                className="flex justify-between items-center"
                              >
                                {(rowData) => (
                                  <div className="flex w-full justify-between items-center">
                                    <div>
                                      {rowData[item?.toLowerCase()]
                                        ? `$${
                                        // Number(
                                        rowData[item?.toLowerCase()]
                                        // ).toFixed(2)
                                        }`
                                        : "-"}
                                    </div>
                                    <div
                                      className={`${getColorForDiff(
                                        rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ]
                                      )}`}
                                    >
                                      {rowData[
                                        `${item?.toLowerCase()}_difference`
                                      ]
                                        ? rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ] + "%"
                                        : "-"}
                                    </div>
                                  </div>
                                )}
                              </Cell>
                            </RSuiteColumn>
                            <RSuiteColumn
                              width={180}
                              align="left"
                              flexGrow={1}
                              verticalAlign="middle"
                            >
                              <HeaderCell>Expected Quote</HeaderCell>
                              <Cell>
                                {(rowData) =>
                                  `${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                  ]
                                    ? `$${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                    ]
                                    }`
                                    : `-`
                                  }`
                                }
                              </Cell>
                            </RSuiteColumn>
                          </>
                        ) : (
                          <>
                            <RSuiteColumn
                              sortable
                              width={180}
                              align="left"
                              verticalAlign="middle"
                            >
                              <HeaderCell
                                renderSortIcon={(sortType) => {
                                  if (sortType === "asc") {
                                    return <img src={SortAsc} />;
                                  } else if (sortType === "desc") {
                                    return <img src={SortDsc} />;
                                  } else return <img src={SortDefault} />;
                                }}
                              >
                                Current Quote
                              </HeaderCell>
                              <Cell
                                dataKey={`${item?.toLowerCase()}_difference`}
                                className="flex justify-between items-center"
                              >
                                {(rowData) => (
                                  <div className="flex w-full justify-between items-center">
                                    <div>
                                      {rowData[item?.toLowerCase()]
                                        ? `$${
                                        // Number(
                                        rowData[item?.toLowerCase()]
                                        // ).toFixed(2)
                                        }`
                                        : "-"}
                                    </div>
                                    <div
                                      className={`${getColorForDiff(
                                        rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ]
                                      )}`}
                                    >
                                      {rowData[
                                        `${item?.toLowerCase()}_difference`
                                      ]
                                        ? rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ] + "%"
                                        : "-"}
                                    </div>
                                  </div>
                                )}
                              </Cell>
                            </RSuiteColumn>
                            <RSuiteColumn
                              width={180}
                              align="left"
                              verticalAlign="middle"
                            >
                              <HeaderCell>Expected Quote</HeaderCell>
                              <Cell>
                                {(rowData) =>
                                  `${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                  ]
                                    ? `$${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                    ]
                                    }`
                                    : `-`
                                  }`
                                }
                              </Cell>
                            </RSuiteColumn>
                          </>
                        )}
                      </ColumnGroup>
                    )}
                  </>
                );
              })}
            {!skuToggle &&
              totalVendors?.length > 0 &&
              totalVendors?.map((item: any) => {
                return (
                  <>
                    {quoteToggle ? (
                      <>
                        {totalVendors?.length >= 1 &&
                          totalVendors?.length <= 3 ? (
                          <RSuiteColumn
                            width={180}
                            flexGrow={1}
                            align="left"
                            verticalAlign="middle"
                          >
                            <HeaderCell>
                              {vendorSummaryData[item?.toUpperCase()] ? (
                                <div className="flex flex-col items-start justify-start">
                                  <span className="font-bold capitalize">
                                    {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                                  </span>
                                  {commodity === 'Copper Wire' ? <>
                                    <span className="italic flex gap-2 items-center font-bold text-[#A3A3A3] text-[1.4vh]">
                                      ({vendorSummaryData[item?.toUpperCase()].contract_validity} - {vendorSummaryData[item?.toUpperCase()].contract_id})
                                      <Whisper
                                        speaker={
                                          <Tooltip placement="auto">
                                            Valid from{" "}
                                            {moment
                                              .utc(
                                                new Date(
                                                  vendorSummaryData[
                                                    item?.toUpperCase()
                                                  ].quotation_start_date
                                                )
                                              )
                                              .format("ll")}
                                          </Tooltip>
                                        }
                                      >
                                        <img
                                          src={ProductInfoIcon}
                                          alt="info"
                                          className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                        ></img>
                                      </Whisper>
                                    </span></> : <span className="italic font-bold text-[#A3A3A3] text-[1.4vh]">
                                    {vendorSummaryData[item?.toUpperCase()]
                                      .quotation_no === 10
                                      ? "Mid Month"
                                      : `Quote #${vendorSummaryData[item?.toUpperCase()]
                                        .quotation_no
                                      }`}{" "}
                                    valid from{" "}
                                    {moment
                                      .utc(
                                        new Date(
                                          vendorSummaryData[
                                            item?.toUpperCase()
                                          ].quotation_start_date
                                        )
                                      )
                                      .format("ll")}
                                  </span>}

                                </div>
                              ) : (
                                <div className="capitalize">
                                  {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                                </div>
                              )}
                            </HeaderCell>
                            <Cell
                              dataKey={`${item?.toLowerCase()}_difference`}
                              className="flex justify-between items-center"
                            >
                              {(rowData) => (
                                <div className="flex w-full justify-between items-center">
                                  <div>
                                    {rowData[item?.toLowerCase()]
                                      ? `${rowData[item?.toLowerCase()]}`
                                      : "-"}
                                  </div>
                                  <div
                                    className={`${getColorForDiff(
                                      rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ]
                                    )}`}
                                  >
                                    {rowData[
                                      `${item?.toLowerCase()}_difference`
                                    ]
                                      ? rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ] + "%"
                                      : "-"}
                                  </div>
                                </div>
                              )}
                            </Cell>
                          </RSuiteColumn>
                        ) : (
                          <RSuiteColumn
                            width={180}
                            align="left"
                            verticalAlign="middle"
                          >
                            <HeaderCell>
                              {vendorSummaryData[item?.toUpperCase()] ? (
                                <div className="flex flex-col items-start justify-start">
                                  <span className="font-bold capitalize">
                                    {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                                  </span>
                                  <span className="italic font-bold text-[#A3A3A3] text-[1.4vh]">
                                    {vendorSummaryData[item?.toUpperCase()]
                                      .quotation_no === 10
                                      ? "Mid Month"
                                      : `Quote #${vendorSummaryData[item?.toUpperCase()]
                                        .quotation_no
                                      }`}{" "}
                                    valid from{" "}
                                    {moment
                                      .utc(
                                        new Date(
                                          vendorSummaryData[
                                            item?.toUpperCase()
                                          ].quotation_start_date
                                        )
                                      )
                                      .format("ll")}
                                  </span>
                                </div>
                              ) : (
                                <div className="capitalize">
                                  {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                                </div>
                              )}
                            </HeaderCell>
                            <Cell
                              dataKey={`${item?.toLowerCase()}_difference`}
                              className="flex justify-between items-center"
                            >
                              {(rowData) => (
                                <div className="flex w-full justify-between items-center">
                                  <div>
                                    {rowData[item?.toLowerCase()]
                                      ? // Number(
                                      rowData[item?.toLowerCase()]
                                      : // ).toFixed(2)
                                      "-"}
                                  </div>
                                  <div
                                    className={`${getColorForDiff(
                                      rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ]
                                    )}`}
                                  >
                                    {rowData[
                                      `${item?.toLowerCase()}_difference`
                                    ]
                                      ? rowData[
                                      `${item?.toLowerCase()}_difference`
                                      ] + "%"
                                      : "-"}
                                  </div>
                                </div>
                              )}
                            </Cell>
                          </RSuiteColumn>
                        )}
                      </>
                    ) : (
                      <ColumnGroup
                        align="center"
                        verticalAlign="middle"
                        groupHeaderHeight={40}
                        header={
                          vendorSummaryData[item?.toUpperCase()] ? (
                            <div className="flex items-center justify-start gap-2 w-full">
                              <span className="font-bold capitalize">
                                {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                              </span>
                              {commodity === 'Copper Wire' ? <>
                                <span className="italic flex gap-2 items-center font-bold text-[#A3A3A3] text-[1.4vh]">
                                  ({vendorSummaryData[item?.toUpperCase()].contract_validity} - {vendorSummaryData[item?.toUpperCase()].contract_id})
                                  <Whisper
                                    speaker={
                                      <Tooltip placement="auto">
                                        Valid from{" "}
                                        {moment
                                          .utc(
                                            new Date(
                                              vendorSummaryData[
                                                item?.toUpperCase()
                                              ].quotation_start_date
                                            )
                                          )
                                          .format("ll")}
                                      </Tooltip>
                                    }
                                  >
                                    <img
                                      src={ProductInfoIcon}
                                      alt="info"
                                      className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                    ></img>
                                  </Whisper>
                                </span></> : <span className="italic font-bold text-[#A3A3A3] text-[1.4vh]">
                                {vendorSummaryData[item?.toUpperCase()]
                                  .quotation_no === 10
                                  ? "Mid Month"
                                  : `Quote #${vendorSummaryData[item?.toUpperCase()]
                                    .quotation_no
                                  }`}{" "}
                                valid from{" "}
                                {moment
                                  .utc(
                                    new Date(
                                      vendorSummaryData[
                                        item?.toUpperCase()
                                      ].quotation_start_date
                                    )
                                  )
                                  .format("ll")}
                              </span>}

                            </div>
                          ) : (
                            <div className="capitalize text-left w-full">
                              {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                            </div>
                          )
                        }
                      >
                        {totalVendors?.length >= 1 &&
                          totalVendors?.length <= 3 ? (
                          <>
                            <RSuiteColumn
                              sortable
                              width={180}
                              flexGrow={1}
                              align="left"
                              verticalAlign="middle"
                            >
                              <HeaderCell
                                renderSortIcon={(sortType) => {
                                  if (sortType === "asc") {
                                    return <img src={SortAsc} />;
                                  } else if (sortType === "desc") {
                                    return <img src={SortDsc} />;
                                  } else return <img src={SortDefault} />;
                                }}
                              >
                                Current Quote
                              </HeaderCell>
                              <Cell
                                dataKey={`${item?.toLowerCase()}_difference`}
                                className="flex justify-between items-center"
                              >
                                {(rowData) => (
                                  <div className="flex w-full justify-between items-center">
                                    <div>
                                      {rowData[item?.toLowerCase()]
                                        ? `$${
                                        // Number(
                                        rowData[item?.toLowerCase()]
                                        // ).toFixed(2)
                                        }`
                                        : "-"}
                                    </div>
                                    <div
                                      className={`${getColorForDiff(
                                        rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ]
                                      )}`}
                                    >
                                      {rowData[
                                        `${item?.toLowerCase()}_difference`
                                      ]
                                        ? rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ] + "%"
                                        : "-"}
                                    </div>
                                  </div>
                                )}
                              </Cell>
                            </RSuiteColumn>
                            <RSuiteColumn
                              width={180}
                              align="left"
                              flexGrow={1}
                              verticalAlign="middle"
                            >
                              <HeaderCell>Expected Quote</HeaderCell>
                              <Cell>
                                {(rowData) =>
                                  `${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                  ]
                                    ? `$${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                    ]
                                    }`
                                    : `-`
                                  }`
                                }
                              </Cell>
                            </RSuiteColumn>
                          </>
                        ) : (
                          <>
                            <RSuiteColumn
                              sortable
                              width={180}
                              align="left"
                              verticalAlign="middle"
                            >
                              <HeaderCell
                                renderSortIcon={(sortType) => {
                                  if (sortType === "asc") {
                                    return <img src={SortAsc} />;
                                  } else if (sortType === "desc") {
                                    return <img src={SortDsc} />;
                                  } else return <img src={SortDefault} />;
                                }}
                              >
                                Current Quote
                              </HeaderCell>
                              <Cell
                                dataKey={`${item?.toLowerCase()}_difference`}
                                className="flex justify-between items-center"
                              >
                                {(rowData) => (
                                  <div className="flex w-full justify-between items-center">
                                    <div>
                                      {rowData[item?.toLowerCase()]
                                        ? `$${
                                        // Number(
                                        rowData[item?.toLowerCase()]
                                        // ).toFixed(2)
                                        }`
                                        : "-"}
                                    </div>
                                    <div
                                      className={`${getColorForDiff(
                                        rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ]
                                      )}`}
                                    >
                                      {rowData[
                                        `${item?.toLowerCase()}_difference`
                                      ]
                                        ? rowData[
                                        `${item?.toLowerCase()}_difference`
                                        ] + "%"
                                        : "-"}
                                    </div>
                                  </div>
                                )}
                              </Cell>
                            </RSuiteColumn>
                            <RSuiteColumn
                              width={180}
                              align="left"
                              verticalAlign="middle"
                            >
                              <HeaderCell>Expected Quote</HeaderCell>
                              <Cell>
                                {(rowData) =>
                                  `${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                  ]
                                    ? `$${rowData[
                                    `${item?.toLowerCase()}_expected_quote`
                                    ]
                                    }`
                                    : `-`
                                  }`
                                }
                              </Cell>
                            </RSuiteColumn>
                          </>
                        )}
                      </ColumnGroup>
                    )}
                  </>
                );
              })}
          </Table>
        ) : (
          <div className="text-center p-10 w-full bg-white">No data found</div>
        )}
      </div>
    </>
  );
};

export default SkuAnalysis;
